import React, { useState, useEffect, useCallback } from 'react';
import { Target, Save, Gift, Megaphone, Trophy, CalendarDays } from 'lucide-react';
import { api, inr, timeAgo } from '../api';
import { toast } from 'sonner';
import BrownieTallyModal from './BrownieTallyModal';
import AdminPointsCalendar from './AdminPointsCalendar';

function Row({ b, onSaved }) {
  const [target, setTarget] = useState(b.target || '');
  const [pct, setPct] = useState(b.brownie_pct ?? 1);
  const [showRedeem, setShowRedeem] = useState(false);
  const [redeemPts, setRedeemPts] = useState('');
  const [reward, setReward] = useState('');

  const save = async () => {
    await api.put(`/targets/${b.agent_id}`, { target: Number(target) || 0, brownie_pct: Number(pct) || 0 });
    toast.success(`🎯 ${b.name}: target ${inr(Number(target) || 0)} @ ${pct}% brownie rate`);
    onSaved();
  };

  const redeem = async () => {
    if (!Number(redeemPts) || !reward.trim()) return toast.error('Points and reward required');
    try {
      const r = await api.post('/redemptions', { agent_id: b.agent_id, points: Number(redeemPts), reward });
      toast.success(`🎁 ${b.name} redeemed ${redeemPts} pts for "${reward}" · balance ${Math.round(r.data.balance)}`);
      setShowRedeem(false); setRedeemPts(''); setReward('');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Redemption failed');
    }
  };

  const above = b.balance >= 0;
  const prog = Math.min(b.progress, 100);

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-50 flex-wrap" data-testid={`target-row-${b.agent_id}`}>
      <span className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-extrabold text-slate-800 border border-white shadow-sm shrink-0" style={{ background: b.avatar_color }}>
        {b.name.split(' ').map((w) => w[0]).join('')}
      </span>
      <span className="text-xs font-bold text-slate-900 w-28 truncate">{b.name}</span>
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-slate-400 font-bold">₹</span>
        <input data-testid={`target-input-${b.agent_id}`} type="number" value={target} placeholder="Daily ₹"
          onChange={(e) => setTarget(e.target.value)}
          className="w-24 border border-slate-200 rounded-lg px-2 py-1 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-amber-500" />
      </div>
      <div className="flex items-center gap-1">
        <input data-testid={`pct-input-${b.agent_id}`} type="number" step="0.1" value={pct}
          onChange={(e) => setPct(e.target.value)}
          className="w-14 border border-slate-200 rounded-lg px-2 py-1 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-amber-500" />
        <span className="text-[10px] text-slate-400 font-bold">%</span>
      </div>
      <button data-testid={`target-save-${b.agent_id}`} onClick={save}
        className="flex items-center gap-1 bg-amber-500 text-white rounded-lg px-2.5 py-1 text-[11px] font-bold hover:bg-amber-400 transition-colors">
        <Save size={11} /> Set
      </button>
      <div className="flex-1 min-w-[140px]">
        {b.target > 0 ? (
          <>
            <div className="flex justify-between text-[10px] font-semibold text-slate-500 mb-0.5">
              <span>Today {inr(b.achieved)} / {inr(b.target)}</span><span>{b.progress}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${prog}%`, background: b.progress >= 100 ? '#10B981' : '#F59E0B' }} />
            </div>
          </>
        ) : (
          <p className="font-hand text-sm text-slate-400">no target yet</p>
        )}
      </div>
      {b.target > 0 && (
        <span data-testid={`points-chip-${b.agent_id}`}
          className={`shrink-0 text-xs font-extrabold px-2.5 py-1 rounded-full ${above ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}
          title={`Earned ${Math.round(b.points)} this month over ${b.days_present} present day${b.days_present === 1 ? '' : 's'} · redeemed ${Math.round(b.redeemed)}`}>
          {above ? '🍫 +' : '🍩 '}{Math.round(b.balance).toLocaleString('en-IN')} pts
        </span>
      )}
      {b.target > 0 && (
        <span className="shrink-0 text-[10px] font-bold text-slate-400" data-testid={`days-present-${b.agent_id}`}>
          {b.days_present}d in</span>
      )}
      {b.target > 0 && (
        <button data-testid={`redeem-button-${b.agent_id}`} onClick={() => setShowRedeem(!showRedeem)}
          className="shrink-0 flex items-center gap-1 bg-violet-600 text-white rounded-lg px-2.5 py-1 text-[11px] font-bold hover:bg-violet-500 transition-colors">
          <Gift size={11} /> Redeem
        </button>
      )}
      {showRedeem && (
        <div className="w-full flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-2.5 py-2 flex-wrap" data-testid={`redeem-form-${b.agent_id}`}>
          <input data-testid={`redeem-points-${b.agent_id}`} type="number" placeholder="Points" value={redeemPts}
            onChange={(e) => setRedeemPts(e.target.value)}
            className="w-20 border border-violet-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-violet-500" />
          <input data-testid={`redeem-reward-${b.agent_id}`} placeholder="Reward (e.g. Amazon voucher ₹500)" value={reward}
            onChange={(e) => setReward(e.target.value)}
            className="flex-1 min-w-[160px] border border-violet-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-violet-500" />
          <button data-testid={`redeem-confirm-${b.agent_id}`} onClick={redeem}
            className="bg-violet-600 text-white rounded-lg px-3 py-1 text-[11px] font-bold hover:bg-violet-500">Cash In 🎁</button>
        </div>
      )}
    </div>
  );
}

export default function AdminTargets() {
  const [rows, setRows] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [showTally, setShowTally] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const load = useCallback(() => {
    api.get('/brownie').then((r) => setRows(r.data));
    api.get('/redemptions').then((r) => setRedemptions(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendRecap = async () => {
    try {
      const r = await api.post('/recap/send');
      toast.success(`📣 Weekly recap (${r.data.week}) sent to ${r.data.sent} agent(s) via chat`);
    } catch {
      toast.error('Failed to send recap');
    }
  };

  return (
    <div data-testid="admin-targets">
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <h3 className="font-display font-bold text-lg text-slate-800 flex items-center gap-2"><Target size={17} className="text-amber-600" /> Daily Targets & Brownie Points</h3>
        <p className="font-hand text-base text-slate-400">daily target · each login day scored (🍫 above / 🍩 below) · absent days skipped</p>
        <button data-testid="points-tally-button" onClick={() => setShowTally(true)}
          className="flex items-center gap-1 bg-amber-500 text-white rounded-lg px-2.5 py-1 text-[11px] font-bold hover:bg-amber-400 transition-colors"
          title="Points earned per agent for any date range, with day-by-day breakdown">
          <Trophy size={11} /> Points Tally
        </button>
        <button data-testid="points-calendar-button" onClick={() => setShowCalendar(true)}
          className="flex items-center gap-1 bg-emerald-600 text-white rounded-lg px-2.5 py-1 text-[11px] font-bold hover:bg-emerald-500 transition-colors"
          title="Per-agent month calendar — mark leave/absent days and adjust points per day">
          <CalendarDays size={11} /> Points Calendar
        </button>
        <button data-testid="send-recap-button" onClick={sendRecap}
          className="flex items-center gap-1 bg-sky-600 text-white rounded-lg px-2.5 py-1 text-[11px] font-bold hover:bg-sky-500 transition-colors"
          title="Sends last week's points, rank & best day to every agent via chat (auto-sends every Monday 9am)">
          <Megaphone size={11} /> Send Weekly Recap
        </button>
      </div>
      <div className="bg-white border-2 border-slate-200 rounded-xl sticky-shadow overflow-hidden">
        {rows.map((b) => <Row key={b.agent_id} b={b} onSaved={load} />)}
      </div>
      {redemptions.length > 0 && (
        <div className="mt-3 bg-white border-2 border-slate-200 rounded-xl sticky-shadow p-3" data-testid="redemption-history">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1"><Gift size={11} className="text-violet-500" /> Recent redemptions</p>
          <div className="space-y-1 max-h-36 overflow-y-auto scroll-thin">
            {redemptions.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-[11px] gap-2">
                <span className="font-bold text-slate-800 shrink-0">{r.agent_name}</span>
                <span className="text-slate-500 truncate flex-1">🎁 {r.reward}</span>
                <span className="font-extrabold text-violet-600 shrink-0">−{Math.round(r.points)} pts</span>
                <span className="text-slate-400 shrink-0">{timeAgo(r.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {showTally && <BrownieTallyModal onClose={() => setShowTally(false)} />}
      {showCalendar && <AdminPointsCalendar onClose={() => setShowCalendar(false)} />}
    </div>
  );
}
